/**
 * `@tento-lona/sheets/internal-ui` — the rows-side package's friend entry for
 * its own front-ends (`@tento/lona/sheets-ui`, `tento-lona-js/sheets-ui/src/`,
 * and the CLI, `tento-lona-js/cli/`) and for the reads the SDK layer
 * (`tento-lona-js/src/`) shares with them, and nothing else.
 *
 * Each friend name has one door: a name here is on no other friend entry
 * (`@tento-lona/sheets/internal-sdk` carries the SDK layer's writers and
 * wiring), so the SDK layer takes `liveRowOf`, `EventsIndex` and
 * `propsFromNodes` from here too (the repository's barrel-tier and one-door
 * checks pin it).
 *
 * What a front-end the SDK ships needs from a rows-side sheet and an
 * application never does — reads only, no writer:
 * - the live row behind a reads face (`liveRowOf(row)`): a render
 *   front-end's binding pipeline (`RenderBinding`, plan rows, a renderer's
 *   `ensureLoaded`) and the CLI's tree walks read the row's variant
 *   class; an application narrows a face with `RowKind.of(row)`;
 * - the implementor names a renderer and the CLI read and an application
 *   never does (the events index, the chart data model) — off
 *   `@tento/lona/sheets/platform`, so the platform barrel is the render
 *   front-end contract only (the render plan's `ColumnKey` is on it: the
 *   published `RenderPlan` names it).
 *
 * A front-end writes a cell the way an application does, through the SDK
 * row's checked verbs (`row.writableSingle()?.write(CellTarget.date(day), value,
 * actor)`, refusal handled), and a formula through `row.update({ formula },
 * actor)`: the renderer variants' writer doors (`columnTextWriterOf`, …) are
 * this repository's tests' only (`@tento-lona/sheets/internal-testing`).
 *
 * A package-private friend entry (shipped, not exported): like
 * `@tento-lona/sheets/internal-sdk`, it ships as a file the published
 * `sheets-ui` and CLI modules import (an exact line in `jsr.json`'s
 * `imports`), and it is NOT in `jsr.json`'s `exports` nor in the root
 * `deno.json`'s import map (it resolves there through the
 * `@tento-lona/sheets/` directory mapping), so a consumer of the package
 * cannot import it; the boundary test (`platform-barrel-stability`) lets only
 * `sheets-ui/src/`, `cli/` and `src/` production code import it.
 */

// ─── The live row behind a reads face ────────────────────────────
export { liveRowOf } from "./rows/model-row.ts";
// The live constituent rows of a list aggregate (`row.as(LIST_AGGREGATE_FACADE)`
// hands out their reads faces): the calendar-list renderer reads each one's
// event origin.
export { liveConstituentRowsOf } from "./facades/list-aggregate-facade.ts";
// The constants' freeze: a published data constant is deeply frozen at its
// definition (`tools/lint/barrel-constants`), in every layer.
export { deepFrozen, frozenMap, frozenSet } from "./core/frozen-collections.ts";
// `Internals`: a class's friend record, registered by its constructor and read
// through an instance (`registry.of(x)`) — how a seam function reaches its own
// class's `#` members without a module-level door, in every layer.
export { Internals } from "./core/internals.ts";
// The UTC days an instant range touches: the one rule the SDK's cell reads
// name (a core utility both friends carry), for the DOM's sheet-wide loads.
export { UtcDays } from "./core/utc-days.ts";
// The data facade's rebuild (`row.as(DATA_FACADE)` hands out reads only): the
// data alias's store rebuilds after its own range fetch.
export { rebuildDataFacade } from "./facades/data-facade.ts";

// ─── Implementor names (off `@tento/lona/sheets/platform`) ───────
// The front-end names only the SDK's own renderers and CLI read: the events
// index, the data-series props reader, the chart data model and its pointer
// store, the render-plan column key.
export { ChartPointerStore } from "@tento-core/chart/chart-pointer-store";
export { DiscreteConvolution } from "@tento-core/chart/discrete-convolution";
export { GraphData } from "@tento-core/chart/graph-data";
export { EventsIndex, propsFromNodes } from "./indices/index.ts";

// The frame scheduler, the TUI's cell adapters and the frame-fact cell source are
// UI-tier names: they live on `@tento-lona/sheets/internal-ui-frame`, so a
// DOM-free read taken from here does not load the frame graph (tento-ui-js's
// frame budget and marks) into the SDK layer's module graph.

// Ordered attributed calendar facts, owned by the front-end's frame snapshot.
export { collectColumnFacts, columnFactKey, captureTaskCalendarMutations, sameTaskCalendarMutations, type TaskCalendarMutations, type ColumnFact, type SourceCellOrigin, type FrameColumnFacts } from "./render/column-facts.ts";
